<!--
  @project E-School Web
  @author Vothana CHY
  @date 2026-07-13
-->

<script lang="ts" setup>
// Section 3 — Benefits About Learning With Us
// Reference: 3. HOME.png
// Layout: warm-gray bg, centered title, 2-col (teacher image left | 3 benefit rows right)

import { computed } from 'vue';

// Static icon imports (Vite requires static paths for assets)
import iconLecturer from '~/assets/icons/lecturer.svg?url';
import iconQuiz from '~/assets/icons/quiz.svg?url';
import iconCloak from '~/assets/icons/cloak.svg?url';
import imgTeacher from '~/assets/images/benifit.hero.png';
import iconComputer from '~/assets/icons/computer.svg?url';
import iconStudent from '~/assets/icons/student.svg?url';
import iconTeaching from '~/assets/icons/teaching.svg?url';

const { t } = useI18n();

const benefits = computed(() => [
  {
    id: 'expert',
    icon: iconLecturer,
    title: t('benefits.expert.title'),
    body: t('benefits.expert.desc'),
  },
  {
    id: 'test',
    icon: iconQuiz,
    title: t('benefits.test.title'),
    body: t('benefits.test.desc'),
  },
  {
    id: 'anywhere',
    icon: iconCloak,
    title: t('benefits.everywhere.title'),
    body: t('benefits.everywhere.desc'),
  },
]);

const doodleIcons = [
  { src: iconComputer, cls: 'absolute top-10 right-16 h-10 w-10 rotate-12' },
  { src: iconQuiz, cls: 'absolute top-6 left-28 h-9 w-9 -rotate-6' },
  { src: iconStudent, cls: 'absolute top-[45%] left-24 h-9 w-9' },
  { src: iconTeaching, cls: 'absolute top-1/3 right-24 h-8 w-8 rotate-45' },
];
</script>

<template>
  <section id="quality" class="w-full bg-[#f0eeeb]">
    <div class="mx-auto max-w-[1440px] px-6 md:px-20">
      <!-- ── Section Title ── -->
      <div class="pt-16 text-center">
        <h2 class="font-ubuntu mb-12 text-[26px] font-extrabold text-gray-900 sm:text-[32px] md:text-[36px]">
          {{ t('benefits.title') }}
        </h2>
      </div>

      <!-- ── 2-col content ── -->
      <div class="grid grid-cols-1 pb-10 md:grid-cols-[45%_55%] md:pb-20">
        <!-- Left: Teacher image + scattered doodles -->
        <div class="relative order-2 flex items-end justify-center pl-0 md:order-1 md:pl-10">
          <!-- Doodle icons scattered behind teacher -->
          <div class="pointer-events-none absolute inset-0 opacity-25">
            <img v-for="(d, i) in doodleIcons" :key="i" :src="d.src" alt="" :class="d.cls" />
          </div>

          <!-- Teacher hero image — no fixed height so image is never clipped -->
          <div class="relative z-10 w-full md:w-[420px]">
            <img :src="imgTeacher" alt="Teacher" class="fade-edges w-full object-contain" />
          </div>
        </div>

        <!-- Right: 3 benefit rows -->
        <div class="order-1 flex flex-col justify-center gap-8 pr-0 md:order-2 md:gap-10 md:pr-24">
          <div v-for="benefit in benefits" :key="benefit.id" class="flex items-start gap-5">
            <!-- Teal circle icon -->
            <div class="flex h-[72px] w-[72px] shrink-0 items-center justify-center rounded-full bg-[#045174]">
              <img :src="benefit.icon" :alt="benefit.title" class="h-9 w-9 brightness-0 invert" />
            </div>

            <!-- Title + body -->
            <div class="flex flex-col">
              <h3 class="font-ubuntu text-[17px] font-semibold text-gray-900">
                {{ benefit.title }}
              </h3>
              <p class="mt-2 text-[13px] leading-[1.7] whitespace-pre-line text-gray-700">
                {{ benefit.body }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.fade-edges {
  -webkit-mask-image:
    linear-gradient(to bottom, black 80%, transparent 100%),
    linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%);
  mask-image:
    linear-gradient(to bottom, black 80%, transparent 100%),
    linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%);
  -webkit-mask-composite: source-in;
  mask-composite: intersect;
}
</style>
